import { CallbackWrap } from '@/types/common'
import { Embed } from '@/types/embed'
import { LevelingEmbed } from '@/types/leveling'
import { getUniqueId } from '@maxroll-media-group/common-libs'
import classNames from 'classnames'
import { Input } from 'components/editor/Input'
import React from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { NumberedMilestoneGroup } from './progression-milestone'
import { ProgressionNavItemEditable } from './progression-nav-item-editable'

let timeout = false

function findGroup(id: string, milestones: Exclude<LevelingEmbed['milestones'], undefined>) {
  for (let groupIndex = 0; groupIndex < milestones.length; groupIndex++) {
    const group = milestones[groupIndex]
    if (group.id === id && 'items' in group) return [groupIndex, group] as const
  }

  return [undefined, undefined] as const
}

export function ProgressionNavGroupEditable({
  group,
  signalMilestone,
  onEmbedChange
}: {
  group: NumberedMilestoneGroup
  signalMilestone: (id: string) => void
  onEmbedChange: (value: CallbackWrap<Embed>) => void
}) {
  const updateOrder = React.useCallback(
    (dragId: string, hoverId: string) => {
      if (!onEmbedChange) return
      onEmbedChange((embed) => {
        if (embed.type !== 'leveling' || !embed.milestones) return embed

        const [dragIndex, draggedItem] = findGroup(dragId, embed.milestones)
        const [hoverIndex, hoveredItem] = findGroup(hoverId, embed.milestones)
        if (!draggedItem || !hoveredItem) return embed

        embed = { ...embed }
        embed.milestones = embed.milestones!.slice()
        embed.milestones[dragIndex] = hoveredItem
        embed.milestones[hoverIndex] = draggedItem

        return embed
      })
    },
    [onEmbedChange]
  )

  const ref = React.useRef(null)
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'poe-progression-nav-group',
      item: () => {
        return { id: group.id }
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging()
      })
    }),
    [group.id]
  )

  const [{ handlerId }, drop] = useDrop<{ id: string }, undefined, { handlerId: string | symbol | null }>(
    () => ({
      accept: 'poe-progression-nav-group',
      collect(monitor) {
        return { handlerId: monitor.getHandlerId() }
      },
      hover(dragItem: { id: string }) {
        if (!ref.current || dragItem.id === group.id) {
          return
        }

        // This requires a timeout since too many updates can break it
        if (!timeout) {
          timeout = true
          updateOrder(dragItem.id, group.id)
          setTimeout(() => {
            timeout = false
          }, 100)
        }
      }
    }),
    [updateOrder, group.id]
  )

  drag(drop(ref))

  return (
    <li
      ref={ref}
      className={classNames(
        'poe-milestone-group poe-milestone-group-editing' /*isDragging && 'poe-milestone-group-dragging'*/
      )}
      data-handler-id={handlerId}>
      <span>
        <Input
          value={group.title}
          onChange={(newValue) => {
            onEmbedChange((embed) => {
              if (embed.type !== 'leveling' || !embed.milestones) return embed
              const [index, milestoneGroup] = findGroup(group.id, embed.milestones)
              if (index === undefined) return embed
              embed = { ...embed }
              embed.milestones = embed.milestones!.slice()
              embed.milestones[index] = {
                ...milestoneGroup,
                title: newValue
              }
              return embed
            })
          }}
        />
        <button
          onClick={() => {
            if (window.confirm('Are you sure you want to delete this?') === true) {
              onEmbedChange((embed) => {
                if (embed.type !== 'leveling' || !embed.milestones) return embed
                const [index] = findGroup(group.id, embed.milestones)
                if (index === undefined) return embed
                embed = { ...embed }
                embed.milestones = embed.milestones!.slice()
                embed.milestones.splice(index, 1)
                return embed
              })
            }
          }}>
          Delete
        </button>
      </span>
      <ul>
        {group.items.map((milestone) => (
          <ProgressionNavItemEditable
            key={milestone.id}
            milestone={milestone}
            signalMilestone={signalMilestone}
            onEmbedChange={onEmbedChange}
          />
        ))}
        <li className={classNames('poe-milestone-item')}>
          <span>
            <button
              onClick={() => {
                onEmbedChange((embed) => {
                  if (embed.type !== 'leveling' || !embed.milestones) return embed
                  const [index, groupMilestone] = findGroup(group.id, embed.milestones)
                  if (index === undefined) return embed
                  embed = { ...embed }
                  embed.milestones = embed.milestones!.slice()
                  const result = { ...groupMilestone }
                  result.items = result.items.slice()
                  result.items.push({
                    id: getUniqueId(),
                    title: 'Enter Milestone Name'
                  })
                  embed.milestones[index] = result
                  return embed
                })
              }}>
              Add milestone
            </button>
          </span>
        </li>
      </ul>
    </li>
  )
}
